import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import de from '../i18n/de.json';
import en from '../i18n/en.json';
import sharedDe from '../vendor/prism-components/i18n/de.json';
import sharedEn from '../vendor/prism-components/i18n/en.json';

/**
 * Shared-i18n-Keys aus prism-components werden in die app-eigenen Dicts gemerged.
 * Die Lib liefert geschachtelte Keys (shared.tenant.list_header) — wir flachen sie aus,
 * damit unsere flache t(key)-Lookup-Logik sie findet.
 *
 * Bei Konflikten gewinnt der app-eigene Key (app-Overrides moeglich).
 */
function flattenKeys(obj, prefix = '') {
  const out = {};
  Object.entries(obj || {}).forEach(([k, v]) => {
    const fullKey = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      Object.assign(out, flattenKeys(v, fullKey));
    } else {
      out[fullKey] = v;
    }
  });
  return out;
}

const DICTS = {
  de: { ...flattenKeys(sharedDe), ...de },
  en: { ...flattenKeys(sharedEn), ...en },
};
const STORAGE_KEY = 'prism_language';

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && DICTS[saved]) return saved;
    return 'de';
  });

  const setLocale = useCallback((next) => {
    if (!DICTS[next]) return;
    localStorage.setItem(STORAGE_KEY, next);
    setLocaleState(next);
    document.documentElement.setAttribute('lang', next);
  }, []);

  const t = useCallback((key, varsOrFallback) => {
    const dict = DICTS[locale] || DICTS.de;
    let str = dict[key];
    // 2. Arg kann ein String-Fallback sein (Library-Signatur t(key, fallback))
    // ODER ein vars-Object (App-Signatur t(key, {tool: "P4A"})).
    if (str === undefined) {
      if (typeof varsOrFallback === 'string') return varsOrFallback;
      str = DICTS.de[key] || key;
    }
    if (varsOrFallback && typeof varsOrFallback === 'object' && typeof str === 'string') {
      Object.keys(varsOrFallback).forEach((k) => {
        str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), varsOrFallback[k]);
      });
    }
    return str;
  }, [locale]);

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used inside LanguageProvider');
  return ctx;
}

export function useTranslation() {
  const { t, locale, setLocale } = useLanguage();
  return { t, locale, setLocale };
}
